import { Header } from '@/components/layout/header'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useSlots } from '@/hooks/useSlots'
import { type DoctorBranchCode, type TakenEvent, api } from '@/lib/api-client'
import { useBranch } from '@/providers/branch-provider'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import * as React from 'react'

export const Route = createFileRoute('/calendar')({
  component: CalendarPage,
})

// ── Grid geometry ───────────────────────────────────────────────────────────
// Rows run 09:00–19:00 (wider than the clinic window) so early background blocks
// are visible; the 10:00–18:00 bookable window is tinted.
const GRID_START_MIN = 9 * 60 // 09:00
const GRID_END_MIN = 19 * 60 // 19:00
const BOOKABLE_START_MIN = 10 * 60 // 10:00
const BOOKABLE_END_MIN = 18 * 60 // 18:00
const SLOT_MIN = 30
const ROW_PX = 44 // height of one 30-min row
const HEADER_PX = 48 // doctor-header / time-gutter header height
const PX_PER_MIN = ROW_PX / SLOT_MIN
const GRID_PX = ((GRID_END_MIN - GRID_START_MIN) / SLOT_MIN) * ROW_PX
// Off-rule (mirrors Nura's availability logic): a doctor is OFF iff zero real
// bookings AND their isNonPatient blocks total >= 5h. Big background blocks with
// patients booked on top are NOT an off-signal.
const OFF_BLOCK_MINUTES = 300

const BRANCH_TABS: Array<{ code: DoctorBranchCode; label: string }> = [
  { code: 'putrajaya', label: 'Putrajaya' },
  { code: 'shah-alam', label: 'Shah Alam' },
  { code: 'bangi', label: 'Bangi' },
  { code: 'kuala-lumpur', label: 'Kuala Lumpur' },
]

interface Column {
  key: string
  name: string
  /** false = an "Other lane" (assistant / virtual / unassigned event lane). */
  isDoctor: boolean
}

/** "HH:MM" (MYT wall-clock) → minutes since midnight, or null if unparseable. */
function parseHHMM(s: string | null): number | null {
  if (!s) return null
  const m = /^(\d{2}):(\d{2})$/.exec(s)
  if (!m) return null
  return Number(m[1]) * 60 + Number(m[2])
}

function fmtHHMM(min: number): string {
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Today in MYT as YYYY-MM-DD. */
function todayMyt(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' })
}

/** Shift a YYYY-MM-DD string by `delta` days (timezone-safe via UTC math). */
function shiftYmd(ymd: string, delta: number): string {
  const [y, mo, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y!, mo! - 1, d!))
  dt.setUTCDate(dt.getUTCDate() + delta)
  return dt.toISOString().slice(0, 10)
}

/** Human "synced X ago" from a UTC ISO string; warn when stale (> 60 min). */
function freshness(syncedAt: string | null): { label: string; warn: boolean } {
  if (!syncedAt) return { label: 'not synced', warn: true }
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(syncedAt)) / 60000))
  if (mins < 1) return { label: 'synced just now', warn: false }
  if (mins < 60) return { label: `synced ${mins} min ago`, warn: false }
  const hrs = Math.floor(mins / 60)
  return { label: `synced ${hrs}h ${mins % 60}m ago`, warn: true }
}

function CalendarPage() {
  const { branchId } = useBranch()

  // Seed the grid branch from the global selector, but fall back to a real
  // branch — the /slots/taken endpoint has no "all" mode.
  const initialBranch: DoctorBranchCode = branchId !== 'all' ? branchId : 'putrajaya'
  const [branchCode, setBranchCode] = React.useState<DoctorBranchCode>(initialBranch)
  const [date, setDate] = React.useState<string>(todayMyt())

  const { data, isLoading, isError, error, isFetching, isPlaceholderData } = useSlots(
    branchCode,
    date
  )

  // Warm the cache for the other branch tabs + adjacent days once the current
  // view has data — tab switches and day-paging then render instantly.
  const queryClient = useQueryClient()
  React.useEffect(() => {
    if (!data) return
    const targets: Array<[DoctorBranchCode, string]> = [
      ...BRANCH_TABS.filter((b) => b.code !== branchCode).map(
        (b) => [b.code, date] as [DoctorBranchCode, string]
      ),
      [branchCode, shiftYmd(date, -1)],
      [branchCode, shiftYmd(date, 1)],
    ]
    for (const [code, d] of targets) {
      queryClient.prefetchQuery({
        queryKey: ['slots', code, d],
        queryFn: () => api.slots.taken(code, d),
        staleTime: 60 * 1000,
      })
    }
  }, [data, branchCode, date, queryClient])

  const timeRows = React.useMemo(() => {
    const rows: number[] = []
    for (let m = GRID_START_MIN; m < GRID_END_MIN; m += SLOT_MIN) rows.push(m)
    return rows
  }, [])

  // Build columns (branch doctors first, then any "other lanes" the taken feed
  // carries on non-doctor lanes) + group events per column key.
  const { columns, eventsByCol, offByCol } = React.useMemo(() => {
    const branchDoctors = data?.branchDoctors ?? []
    const taken = data?.taken ?? []

    const cols: Column[] = branchDoctors.map((d) => ({
      key: d.doctorKey,
      name: d.name,
      isDoctor: true,
    }))

    // Rostered non-doctor lanes (CA / virtual / custom columns) get standing
    // columns even with zero events today — staff see the lane exists.
    for (const l of data?.otherLanes ?? []) {
      cols.push({ key: l.doctorKey, name: l.name, isDoctor: false })
    }
    const knownKeys = new Set(cols.map((c) => c.key))

    // colKey resolver: a taken row lands in its known-lane column; an event on
    // a lane the roster doesn't know yet still forms an ad-hoc "Other lane".
    const colKeyFor = (t: TakenEvent): string =>
      t.doctorKey && knownKeys.has(t.doctorKey) ? t.doctorKey : (t.doctorKey ?? '__unassigned')

    const extra = new Map<string, string>()
    for (const t of taken) {
      if (t.doctorKey && knownKeys.has(t.doctorKey)) continue
      const k = colKeyFor(t)
      if (!extra.has(k)) extra.set(k, t.doctorName?.trim() || 'Unassigned')
    }
    for (const [key, name] of extra) cols.push({ key, name, isDoctor: false })

    const byCol = new Map<string, TakenEvent[]>()
    for (const t of taken) {
      const k = colKeyFor(t)
      const arr = byCol.get(k)
      if (arr) arr.push(t)
      else byCol.set(k, [t])
    }

    // Off-rule per doctor column.
    const off = new Map<string, boolean>()
    for (const col of cols) {
      if (!col.isDoctor) continue
      const evs = byCol.get(col.key) ?? []
      const realBookings = evs.filter((e) => !e.isNonPatient)
      let blockMin = 0
      for (const e of evs) {
        if (!e.isNonPatient) continue
        const s = parseHHMM(e.start)
        const en = e.end ? parseHHMM(e.end) : null
        if (s != null && en != null && en > s) blockMin += en - s
      }
      off.set(col.key, realBookings.length === 0 && blockMin >= OFF_BLOCK_MINUTES)
    }

    return { columns: cols, eventsByCol: byCol, offByCol: off }
  }, [data])

  const chip = freshness(data?.syncedAt ?? null)

  return (
    <div className="flex flex-col h-full">
      <Header title="Calendar" subtitle="Per-doctor availability from Avixo" />

      <div className="flex-1 p-3 sm:p-4 lg:p-6 space-y-4 overflow-y-auto">
        {/* Toolbar: branch tabs + date nav + freshness chip */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Branch selector */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            {BRANCH_TABS.map((b) => (
              <button
                key={b.code}
                type="button"
                onClick={() => setBranchCode(b.code)}
                className={`rounded-md px-3 py-1 text-[13px] transition-colors ${
                  branchCode === b.code
                    ? 'bg-primary text-primary-foreground'
                    : 'text-text-muted hover:bg-hover'
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>

          {/* Date nav */}
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            <button
              type="button"
              onClick={() => setDate((d) => shiftYmd(d, -1))}
              className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-hover"
              title="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <input
              type="date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              className="bg-transparent px-2 text-[13px] text-text-dark outline-none"
            />
            <button
              type="button"
              onClick={() => setDate((d) => shiftYmd(d, 1))}
              className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-hover"
              title="Next day"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setDate(todayMyt())}
            className="rounded-lg border border-border bg-card px-3 py-1.5 text-[13px] text-text-muted hover:bg-hover"
          >
            Today
          </button>

          {/* Freshness chip */}
          <span
            className={`ml-auto inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] ${
              chip.warn
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                : 'border-border bg-card text-text-muted'
            }`}
            title={data?.syncedAt ?? undefined}
          >
            <CalendarDays className="h-3.5 w-3.5" />
            {chip.label}
          </span>
        </div>

        {/* States */}
        {isLoading && <GridSkeleton />}

        {isError && !isLoading && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center text-[13px] text-destructive">
            Failed to load calendar: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        )}

        {!isLoading && !isError && data?.closed && (
          <div className="rounded-xl border border-border bg-card p-10 text-center">
            <div className="text-[15px] font-semibold text-text-dark">Clinic closed</div>
            <div className="mt-1 text-[13px] text-text-muted">
              No bookable slots for {date}
              {data.weekday === 1 ? ' (Mondays are closed)' : ''}.
            </div>
          </div>
        )}

        {!isLoading && !isError && data && !data.closed && columns.length === 0 && (
          <div className="rounded-xl border border-border bg-card p-8 text-center text-text-muted">
            No doctor lanes for this branch/day. Run the Avixo calendar sync.
          </div>
        )}

        {!isLoading && !isError && data && !data.closed && columns.length > 0 && (
          <TooltipProvider delayDuration={150}>
            <div
              className={`overflow-x-auto rounded-xl border border-border bg-card transition-opacity ${
                isFetching && isPlaceholderData ? 'opacity-50' : ''
              }`}
            >
              <div className="flex min-w-max">
                {/* Time gutter (sticky first column) */}
                <div className="sticky left-0 z-20 w-16 flex-shrink-0 border-r border-border bg-card">
                  <div
                    style={{ height: HEADER_PX }}
                    className="flex items-center justify-center border-b border-border text-[11px] font-medium uppercase tracking-wide text-text-muted"
                  >
                    Time
                  </div>
                  <div className="relative" style={{ height: GRID_PX }}>
                    {/* Row cells mirror the doctor-column gridlines so each label
                      reads as the row's start time, never clipped. */}
                    {timeRows.map((min, i) => {
                      const bookable = min >= BOOKABLE_START_MIN && min < BOOKABLE_END_MIN
                      const onHour = min % 60 === 0
                      return (
                        <div
                          key={min}
                          className={`absolute inset-x-0 border-b pr-2 pt-0.5 text-right tabular-nums ${
                            onHour ? 'border-border' : 'border-border/40'
                          } ${
                            onHour
                              ? bookable
                                ? 'text-[12px] font-semibold text-text-dark'
                                : 'text-[12px] font-medium text-text-muted'
                              : bookable
                                ? 'text-[10px] text-text-muted'
                                : 'text-[10px] text-text-muted/50'
                          }`}
                          style={{ top: i * ROW_PX, height: ROW_PX }}
                        >
                          {fmtHHMM(min)}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Doctor columns */}
                {columns.map((col) => (
                  <DoctorColumn
                    key={col.key}
                    column={col}
                    events={eventsByCol.get(col.key) ?? []}
                    off={offByCol.get(col.key) ?? false}
                    timeRows={timeRows}
                  />
                ))}
              </div>
            </div>
          </TooltipProvider>
        )}

        {/* Legend */}
        {!isLoading && !isError && data && !data.closed && columns.length > 0 && (
          <div className="flex flex-wrap items-center gap-4 text-[12px] text-text-muted">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-primary/80" /> Booking
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block h-3 w-3 rounded-sm border border-border"
                style={{ backgroundImage: STRIPE_BG }}
              />{' '}
              Block (doctor off / covering)
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm bg-info/10 ring-1 ring-inset ring-info/20" />{' '}
              10:00–18:00 bookable window
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// Muted diagonal stripes for background blocks.
const STRIPE_BG =
  'repeating-linear-gradient(45deg, rgba(120,120,120,0.14) 0, rgba(120,120,120,0.14) 6px, transparent 6px, transparent 12px)'

function DoctorColumn({
  column,
  events,
  off,
  timeRows,
}: {
  column: Column
  events: TakenEvent[]
  off: boolean
  timeRows: number[]
}) {
  const blocks = events.filter((e) => e.isNonPatient)
  const bookings = events.filter((e) => !e.isNonPatient)

  return (
    <div
      className={`w-44 flex-shrink-0 border-r border-border last:border-r-0 ${off ? 'opacity-60' : ''}`}
    >
      {/* Header */}
      <div
        style={{ height: HEADER_PX }}
        className="flex items-center justify-between gap-1 border-b border-border px-2"
      >
        <div className="min-w-0">
          <div className="truncate text-[12px] font-semibold text-text-dark" title={column.name}>
            {column.name}
          </div>
          {!column.isDoctor && (
            <div className="text-[10px] uppercase tracking-wide text-text-muted">Other lane</div>
          )}
        </div>
        {off && (
          <Badge
            variant="outline"
            className="border-amber-500/40 text-[10px] text-amber-600 dark:text-amber-400"
          >
            OFF
          </Badge>
        )}
      </div>

      {/* Track */}
      <div className="relative" style={{ height: GRID_PX }}>
        {/* Bookable-window tint (bottom layer) */}
        <div
          className="absolute inset-x-0 bg-info/5"
          style={{
            top: (BOOKABLE_START_MIN - GRID_START_MIN) * PX_PER_MIN,
            height: (BOOKABLE_END_MIN - BOOKABLE_START_MIN) * PX_PER_MIN,
          }}
        />
        {/* Hour/half-hour gridlines */}
        {timeRows.map((min, i) => (
          <div
            key={min}
            className={`absolute inset-x-0 border-b ${
              min % 60 === 0 ? 'border-border' : 'border-border/40'
            }`}
            style={{ top: i * ROW_PX, height: ROW_PX }}
          />
        ))}

        {/* Background blocks */}
        {blocks.map((e) => {
          const pos = eventPos(e)
          if (!pos) return null
          return (
            <Tooltip key={`b-${e.start}-${e.end ?? ''}-${e.content ?? e.patientName ?? ''}`}>
              <TooltipTrigger asChild>
                <div
                  className="absolute inset-x-0.5 overflow-hidden rounded-sm border border-border/50 px-1 py-0.5 text-[10px] leading-tight text-text-muted"
                  style={{ top: pos.top, height: pos.height, backgroundImage: STRIPE_BG }}
                >
                  <span className="line-clamp-2">{e.content || e.patientName || 'Block'}</span>
                </div>
              </TooltipTrigger>
              <EventTooltipContent e={e} laneName={column.name} />
            </Tooltip>
          )
        })}

        {/* Foreground bookings — overlapping events split side-by-side */}
        {layoutBookings(bookings).map(({ e, top, height, lane, lanes }) => (
          <Tooltip key={`p-${e.start}-${e.end ?? ''}-${e.patientName ?? e.content ?? ''}-${lane}`}>
            <TooltipTrigger asChild>
              <div
                className="absolute z-10 overflow-hidden rounded-md border border-primary/40 bg-primary/15 px-1.5 py-0.5 text-[11px] leading-tight text-text-dark shadow-sm"
                style={{
                  top,
                  height,
                  left: `calc(${(lane / lanes) * 100}% + 2px)`,
                  width: `calc(${100 / lanes}% - 4px)`,
                }}
              >
                <div className="truncate font-medium">
                  {e.patientName || e.content || 'Booking'}
                </div>
                {height > 24 && lanes === 1 && (
                  <div className="text-[10px] text-text-muted tabular-nums">{e.start}</div>
                )}
              </div>
            </TooltipTrigger>
            <EventTooltipContent e={e} laneName={column.name} />
          </Tooltip>
        ))}
      </div>
    </div>
  )
}

/** Hover detail card — full info that doesn't fit in the (possibly half-width) chip. */
function EventTooltipContent({ e, laneName }: { e: TakenEvent; laneName: string }) {
  const time = `${e.start}${e.end ? ` – ${e.end}` : ''}`
  return (
    <TooltipContent side="right" className="max-w-72 space-y-0.5 py-2">
      <div className="font-semibold">
        {e.isNonPatient ? e.content || 'Block' : e.patientName || 'Booking'}
      </div>
      <div className="tabular-nums opacity-90">
        {time} · {laneName}
      </div>
      {!e.isNonPatient && e.visitReason && <div>Proc: {e.visitReason}</div>}
      {!e.isNonPatient && e.mobile && <div className="tabular-nums">Tel: {e.mobile}</div>}
      {!e.isNonPatient && e.comment && <div className="opacity-90">“{e.comment}”</div>}
      {e.isNonPatient && <div className="opacity-90">Block (doctor off / covering / reserved)</div>}
    </TooltipContent>
  )
}

/** Assign overlapping bookings to side-by-side sub-lanes (classic calendar
 * cluster layout): sort by start, greedily reuse the first free sub-lane;
 * every event in an overlap cluster shares the cluster's lane count. */
function layoutBookings(
  bookings: TakenEvent[]
): Array<{ e: TakenEvent; top: number; height: number; lane: number; lanes: number }> {
  type Item = { e: TakenEvent; top: number; height: number; s: number; en: number; lane: number }
  const items: Item[] = []
  for (const e of bookings) {
    const pos = eventPos(e)
    const s = parseHHMM(e.start)
    if (!pos || s == null) continue
    const en = e.end ? (parseHHMM(e.end) ?? s + SLOT_MIN) : s + SLOT_MIN
    items.push({ e, top: pos.top, height: pos.height, s, en, lane: 0 })
  }
  items.sort((a, b) => a.s - b.s || b.en - a.en)

  const out: Array<{ e: TakenEvent; top: number; height: number; lane: number; lanes: number }> = []
  let cluster: Item[] = []
  let laneEnds: number[] = []
  let clusterEnd = -1
  const flush = () => {
    const lanes = Math.max(laneEnds.length, 1)
    for (const it of cluster)
      out.push({ e: it.e, top: it.top, height: it.height, lane: it.lane, lanes })
    cluster = []
    laneEnds = []
  }
  for (const it of items) {
    if (cluster.length > 0 && it.s >= clusterEnd) flush()
    let lane = laneEnds.findIndex((end) => end <= it.s)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(it.en)
    } else {
      laneEnds[lane] = it.en
    }
    it.lane = lane
    cluster.push(it)
    clusterEnd = cluster.length === 1 ? it.en : Math.max(clusterEnd, it.en)
  }
  flush()
  return out
}

/** Absolute top/height (px) for an event within the grid, clamped to the window. */
function eventPos(e: TakenEvent): { top: number; height: number } | null {
  const s0 = parseHHMM(e.start)
  if (s0 == null) return null
  const e0 = e.end ? (parseHHMM(e.end) ?? s0 + SLOT_MIN) : s0 + SLOT_MIN
  const start = Math.max(GRID_START_MIN, Math.min(s0, GRID_END_MIN))
  const end = Math.max(start, Math.min(e0, GRID_END_MIN))
  const top = (start - GRID_START_MIN) * PX_PER_MIN
  const height = Math.max((end - start) * PX_PER_MIN, 16) // floor for visibility
  return { top, height }
}

const SKELETON_COLS = ['c1', 'c2', 'c3', 'c4']
const SKELETON_ROWS = ['r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8']

function GridSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex gap-2">
        {SKELETON_COLS.map((k) => (
          <div key={k} className="h-12 flex-1 animate-pulse rounded-md bg-hover" />
        ))}
      </div>
      <div className="space-y-2">
        {SKELETON_ROWS.map((k) => (
          <div key={k} className="h-8 animate-pulse rounded-md bg-hover" />
        ))}
      </div>
    </div>
  )
}
